import { useState, useEffect, useCallback } from 'react'

import { toast } from 'react-toastify'
import { axiosInstance } from '../helpers/axios'
import {
    HelpVideo,
    InitialTrainingProgress,
    InitialTrainingVideo,
    UpdateInitialTrainingProgressRequest,
    UpdateInitialTrainingVideoRequest,
} from '../navigation/layouts/Lori/types'

export const useTrainingVideo = () => {
    const [loading, setLoading] = useState(false)
    const [videoLibrary, setVideoLibrary] = useState<HelpVideo[]>([])
    const [categories, setCategories] = useState<string[]>([])
    const [
        initialTrainingProgress,
        setInitialTrainingProgress,
    ] = useState<InitialTrainingProgress | null>(null)
    const [initialTrainingVideos, setInitialTrainingVideos] = useState<
        InitialTrainingVideo[]
    >([])

    const addVideo = async (url: string, category: string) => {
        await axiosInstance
            .post('/chat/help_video_library/', { url, category })
            .then((res) => {
                const video = res.data as HelpVideo
                setVideoLibrary((prev) => [...prev, video])
                toast.success('Video added')
            })
            .catch((err) => {
                console.log(err)
            })
    }

    const removeVideo = async (id: number) => {
        await axiosInstance
            .delete(`/chat/help_video_library/${id}/`)
            .then((res) => {
                toast.success('Video removed')
            })
            .catch((err) => {
                console.log(err)
            })
    }

    const updateVideo = async (helpVideo: HelpVideo) => {
        await axiosInstance
            .put(`/chat/help_video_library/${helpVideo.id}/`, helpVideo)
            .then((res) => {
                setVideoLibrary((prevLibrary) =>
                    prevLibrary.map((video) =>
                        video.id === helpVideo.id ? helpVideo : video,
                    ),
                )
                toast.success('Video updated')
            })
            .catch((err) => {
                console.log(err)
            })
    }

    const addVideoView = async (id: number) => {
        await axiosInstance
            .post(`/chat/help_video_library/${id}/add_video_view/`)
            .then((res) => {
                console.log('video view added', res.data)
            })
            .catch((err) => {
                console.log(err)
            })
    }

    const getVideoLibrary = async () => {
        setLoading(true)
        await axiosInstance
            .get('/chat/help_video_library/')
            .then((res) => {
                setVideoLibrary(res.data)
            })
            .catch((err) => {
                toast.error('Error fetching video library')
            })
            .finally(() => {
                setLoading(false)
            })
    }

    const getCategories = async () => {
        await axiosInstance
            .get('/chat/help_video_library/categories/')
            .then((res) => {
                const formattedCategories = res.data.map((category: string) => {
                    // Split by underscore, capitalize first letter of each word, then join with space
                    return category
                        .split('_')
                        .map(
                            (word) =>
                                word.charAt(0).toUpperCase() + word.slice(1),
                        )
                        .join(' ')
                })
                setCategories(formattedCategories)
            })
            .catch((err) => {
                console.log(err)
            })
    }

    const addCategory = async (category: string) => {
        await axiosInstance
            .post('/chat/help_video_library/add_category/', { category })
            .then((res) => {
                const newCategory = res.data.category
                setCategories([...categories, newCategory])
                toast.success('Category added')
            })
            .catch((err) => {
                toast.error('Error adding category')
            })
    }

    const getOrCreateInitialUserTrainingProgress = async () => {
        await axiosInstance
            .get('/chat/initial-training-progress/get_or_create/')
            .then((res) => {
                setInitialTrainingProgress(res.data)
            })
            .catch((err) => {
                toast.error(err.response.data.message)
            })
    }

    const updateInitialUserTrainingProgress = async (
        request: UpdateInitialTrainingProgressRequest,
    ) => {
        await axiosInstance
            .patch(
                `/chat/initial-training-progress/${request.id}/`,
                request.body,
            )
            .then((res) => {
                setInitialTrainingProgress(res.data)
            })
            .catch((err) => {
                toast.error(err.response.data.message)
            })
    }

    const getInitialTrainingVideos = async () => {
        await axiosInstance
            .get('/chat/initial-training-video/')
            .then((res) => {
                setInitialTrainingVideos(res.data)
            })
            .catch((err) => {
                toast.error(err.response.data.message)
            })
    }

    const updateInitialTrainingVideos = async (
        request: UpdateInitialTrainingVideoRequest,
    ) => {
        await axiosInstance
            .post('/chat/initial-training-video/', request)
            .then((res) => {
                setInitialTrainingVideos(res.data)
            })
            .catch((err) => {
                toast.error(err.response.data.message)
            })
    }

    return {
        loading,
        videoLibrary,
        categories,
        initialTrainingProgress,
        initialTrainingVideos,
        addVideo,
        removeVideo,
        updateVideo,
        addVideoView,
        getVideoLibrary,
        getCategories,
        addCategory,
        getOrCreateInitialUserTrainingProgress,
        updateInitialUserTrainingProgress,
        getInitialTrainingVideos,
        updateInitialTrainingVideos,
    }
}

export type TrainingVideoController = ReturnType<typeof useTrainingVideo>
